import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Popup from 'reactjs-popup';
import { useDispatch } from "react-redux";
import { annulerPolitique } from "../../server/services/politique-service";
import { getTermesEtCondition } from "../../server/services/unite-service";
import { setAlert } from "../actions/alert";
import PopupHeader from "../popups/popup-header.js";
import PopupFooter from "../popups/popup-footer.js";
import Translator from "../translator";
import "./annulation-popup.css";


function parseAnnulationResponse(res) {
  if (res === true || res === false) {
    return res;
  }
  if (res === "true" || res === "false") {
    return res === "true";
  }
  return null;
}

function formatDate(value) {
  if (value == null) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatDateLong(value, language = "fr") {
  if (value == null) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  const locale = language === "fr" ? "fr-FR" : "en-CA";
  const str = d.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

function formatTime(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const parts = value.split(":");
    return `${parts[0] || "00"}:${parts[1] || "00"}`;
  }
  return String(value);
}

/** Keeps only safe inline tags: b, i, u, strong, em, br. Removes all other HTML. */
function sanitizeTermesHtml(html) {
  if (typeof html !== "string") return "";
  return html.replace(/<[^>]+>/g, (tag) => {
    const normalized = tag.toLowerCase().replace(/\s+/g, " ").trim();
    if (/^<\/?(b|i|u|strong|em)>$/.test(normalized)) return tag;
    if (/^<br\s*\/?>$/.test(normalized)) return "<br />";
    return "";
  });
}

const ANNULATION_COUNTDOWN_SECONDS = 5;

function confirmationNoShown(prop, row) {
  return (
    String(prop ?? "").trim() ||
    String(row?.noConfirmation ?? row?.no_confirmation ?? row?.NoConfirmation ?? "").trim() ||
    "—"
  );
}

function AnnulationPopup(props) {
  const dispatch = useDispatch();
  const { isOpen, facture, handleClose, language = "fr", noConfirmation } = props;
  const [termesEtConditionText, setTermesEtConditionText] = useState("");
  const [isRequesting, setIsRequesting] = useState(false);
  const [countdownRemaining, setCountdownRemaining] = useState(null);
  const [showSuccessConfirm, setShowSuccessConfirm] = useState(false);
  const annulRequestCancelledRef = useRef(false);
  const annulFromCountdownStartedRef = useRef(false);

  const t = useMemo(() => Translator(language, "TXT_ANNULATION_POPUP"), [language]);

  useEffect(() => {
    if (!isOpen || !facture?.[0]) {
      setTermesEtConditionText("");
      return;
    }
    const idUnite = facture[0].idUnite ?? facture[0].id_unite;
    if (!idUnite) {
      setTermesEtConditionText("");
      return;
    }
    let cancelled = false;
    getTermesEtCondition(idUnite, language)
      .then((text) => {
        if (!cancelled) setTermesEtConditionText(text != null ? String(text) : "");
      })
      .catch(() => {
        if (!cancelled) setTermesEtConditionText("");
      });
    return () => { cancelled = true; };
  }, [isOpen, facture, language]);

  useEffect(() => {
    if (!isOpen) {
      annulRequestCancelledRef.current = true;
      annulFromCountdownStartedRef.current = false;
      setIsRequesting(false);
      setCountdownRemaining(null);
      setShowSuccessConfirm(false);
    } else {
      annulRequestCancelledRef.current = false;
      annulFromCountdownStartedRef.current = false;
    }
  }, [isOpen]);

  const runAnnulerPolitique = useCallback(() => {
    if (!facture?.[0] || annulRequestCancelledRef.current) {
      return;
    }
    setIsRequesting(true);
    annulerPolitique(facture[0].idReservationDetail, facture[0].idReservationEntete)
      .then((res) => {
        if (annulRequestCancelledRef.current) {
          return;
        }
        const parsed = parseAnnulationResponse(res);

        if (parsed === true) {
          setShowSuccessConfirm(true);
          return;
        }

        if (parsed === false) {
          dispatch(setAlert(t.cancelFailed, "danger"));
          return;
        }

        dispatch(setAlert(t.cancelUnexpected, "danger"));
      })
      .catch(() => {
        if (!annulRequestCancelledRef.current) {
          dispatch(setAlert(t.cancelNetwork, "danger"));
        }
      })
      .finally(() => {
        if (!annulRequestCancelledRef.current) {
          setIsRequesting(false);
        }
      });
  }, [dispatch, facture, t]);

  useEffect(() => {
    if (countdownRemaining === null) {
      return undefined;
    }
    if (countdownRemaining === 0) {
      if (!annulFromCountdownStartedRef.current && !annulRequestCancelledRef.current) {
        annulFromCountdownStartedRef.current = true;
        runAnnulerPolitique();
      }
      setCountdownRemaining(null);
      return undefined;
    }
    const id = window.setTimeout(() => {
      setCountdownRemaining((c) => {
        if (c === null || c < 1) {
          return c;
        }
        return c - 1;
      });
    }, 1000);
    return () => window.clearTimeout(id);
  }, [countdownRemaining, runAnnulerPolitique]);

  const confirm = () => {
    if (!facture?.[0] || countdownRemaining !== null || isRequesting) {
      return;
    }
    annulFromCountdownStartedRef.current = false;
    setCountdownRemaining(ANNULATION_COUNTDOWN_SECONDS);
  };

  const cancelCountdown = () => {
    if (isRequesting) {
      return;
    }
    setCountdownRemaining(null);
  };

  const onFooterClick = () => {
    if (countdownRemaining !== null && !isRequesting) {
      cancelCountdown();
      return;
    }
    confirm();
  };

  const footerTitle = isRequesting
    ? t.cancelInProgress
    : countdownRemaining !== null
      ? t.cancelDelayWaiting
      : t.confirmFooter;

  const closeSuccessAndParent = () => {
    setShowSuccessConfirm(false);
    handleClose();
  };

  console.log(facture);
  return (
    <Popup
      content="test"
      on='click'
      open={isOpen}
      onClose={handleClose}
      modal
      nested
    >
      {close => (
        <div className="annulation-popup-overlay">
          <div className="annulation-popup-backdrop" onClick={handleClose} aria-hidden="true" />

          {showSuccessConfirm ? (
            <div
              className="annulation-popup-success-layer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="annulation-popup-success-title"
            >
              <div className="annulation-popup-success-backdrop" aria-hidden="true" />
              <div className="annulation-popup-success-card">
                <h2 id="annulation-popup-success-title" className="annulation-popup-success-title">
                  {t.successTitle}
                </h2>
                <p className="annulation-popup-success-text">{t.successMessage}</p>
                <button type="button" className="annulation-popup-success-ok" onClick={closeSuccessAndParent}>
                  {t.successOk}
                </button>
              </div>
            </div>
          ) : null}

          <div className="annulation-popup-wrapper">
            <div className="annulation-popup-card item-popup-background">
              <PopupHeader
                title={t.titleContract}
                onClick={handleClose}
              />
              {facture && (
                <div className="annulation-popup-body">
                  <section className="annulation-popup-summary" aria-label={t.summaryAria}>
                    <dl className="annulation-popup-dl">
                      <div className="annulation-popup-row">
                        <dt>{t.confirmationNo}</dt>
                        <dd>{confirmationNoShown(noConfirmation, facture[0])}</dd>
                      </div>
                      <div className="annulation-popup-row">
                        <dt>{t.arrival}</dt>
                        <dd>
                          {formatDateLong(facture[0].dateDebut, language)} {t.timeAt} {formatTime(facture[0].heureArrivee)}
                          {t.timeSuffix ? ` ${t.timeSuffix}` : ""}
                        </dd>
                      </div>
                      <div className="annulation-popup-row">
                        <dt>{t.departure}</dt>
                        <dd>
                          {formatDateLong(facture[0].dateFin, language)} {t.timeAt} {formatTime(facture[0].heureDepart)}
                          {t.timeSuffix ? ` ${t.timeSuffix}` : ""}
                        </dd>
                      </div>
                      <div className="annulation-popup-row">
                        <dt>{t.travelers}</dt>
                        <dd>
                          {(facture[0].nombreAdultes ?? facture[0].nombre_adultes)} {t.travelersAdultsLabel}, {(facture[0].nombreEnfants ?? facture[0].nombre_enfants)} {t.travelersChildrenLabel}
                        </dd>
                      </div>
                    </dl>
                  </section>
                  <section className="annulation-popup-termes-section" aria-label={t.termesAria}>
                    {termesEtConditionText ? (
                      <div
                        className="annulation-popup-termes"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeTermesHtml(termesEtConditionText)
                        }}
                      />
                    ) : (
                      <p className="annulation-popup-termes-loading">{t.loadingTermes}</p>
                    )}
                  </section>
                </div>
              )}
              {termesEtConditionText &&
                <PopupFooter
                  title={footerTitle}
                  onClick={onFooterClick}
                  isLoading={isRequesting}
                  showSpinner={countdownRemaining !== null && !isRequesting}
                />
              }
            </div>
          </div>
        </div>
      )}
    </Popup>
  );
}

export default AnnulationPopup;